import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { CiMultiselectComponent } from '../ci-multiselect/ci-multiselect.component';

export interface CiOrderedItem {
  id: string;
  name: string;
}

export interface CiOrderedMove {
  id: string;
  from: number;
  to: number;
  total: number;
}

export interface CiOrderedRemoval {
  id: string;
  position: number;
  total: number;
}

@Component({
  selector: 'app-ci-ordered-multiselect',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    MatIcon,
    CiMultiselectComponent,
  ],
  templateUrl: './ci-ordered-multiselect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CiOrderedMultiselectComponent {
  private readonly _items = signal<readonly CiOrderedItem[]>([]);
  private readonly _selectedIds = signal<string[]>([]);

  @Input({ required: true })
  set items(value: readonly CiOrderedItem[]) {
    this._items.set(value ?? []);
  }
  get items(): readonly CiOrderedItem[] {
    return this._items();
  }

  @Input({ required: true })
  set selectedIds(value: readonly string[]) {
    this._selectedIds.set([...(value ?? [])]);
  }
  get selectedIds(): string[] {
    return this._selectedIds();
  }

  @Input() label = '';
  @Input() required = false;
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() emptyLabel = '';
  @Input() clearLabel = '';
  @Input() summaryTemplate = '';
  @Input() orderHeaderLabel = '';
  @Input() orderHintLabel = '';
  @Input() emptyListLabel = '';
  @Input() addButtonLabel = '';
  @Input() dragHandleAriaLabel = '';
  @Input() orderListAriaLabel = '';
  @Input() invalid = false;
  @Input() errorText = '';
  @Input() maxItems: number | null = null;

  /** Sortierliste ausblenden, wo nur die Auswahl zaehlt (Projekt, CV-Import). */
  @Input() showSorting = true;

  @Input() moveUpAriaLabel: (item: string, position: number, total: number) => string =
    (item) => item;
  @Input() moveDownAriaLabel: (item: string, position: number, total: number) => string =
    (item) => item;
  @Input() deleteAriaLabel: (item: string, position: number, total: number) => string =
    (item) => item;

  @Output() selectedIdsChange = new EventEmitter<string[]>();
  @Output() addClicked = new EventEmitter<void>();

  /** Ids, die gerade dazugekommen sind — fuer Ansagen im Aufrufer. */
  @Output() itemsAdded = new EventEmitter<string[]>();
  @Output() itemsRemoved = new EventEmitter<string[]>();
  @Output() itemRemoved = new EventEmitter<CiOrderedRemoval>();
  @Output() itemMoved = new EventEmitter<CiOrderedMove>();

  /** Id des Eintrags, der die Obergrenze gerissen haette. */
  @Output() maxExceeded = new EventEmitter<string>();

  private readonly nameById = computed(
    () => new Map(this._items().map((i) => [i.id, i.name])),
  );

  protected readonly itemIds = computed(() => this._items().map((i) => i.id));

  protected readonly rows = computed(() => {
    const names = this.nameById();
    const ids = this._selectedIds();
    return ids.map((id, index) => ({
      id,
      name: names.get(id) ?? id,
      position: index + 1,
      total: ids.length,
    }));
  });

  displayName = (id: string): string => this.nameById().get(id) ?? id;

  protected onPanelChange(newIds: string[]): void {
    const current = this._selectedIds();
    const added = newIds.filter((id) => !current.includes(id));

    if (
      this.maxItems !== null &&
      current.length + added.length > this.maxItems &&
      added.length > 0
    ) {
      this.maxExceeded.emit(added[0]);
      // Undo the add: reset internal state so ci-multiselect writes back.
      this._selectedIds.set([...current]);
      this.selectedIdsChange.emit([...current]);
      return;
    }

    // Preserve existing order; append additions at the end.
    const stillSelected = current.filter((id) => newIds.includes(id));
    const removed = current.filter((id) => !newIds.includes(id));
    const next = [...stillSelected, ...added];
    this._selectedIds.set(next);
    this.selectedIdsChange.emit(next);
    if (added.length > 0) this.itemsAdded.emit(added);
    if (removed.length > 0) this.itemsRemoved.emit(removed);
  }

  protected moveUp(index: number): void {
    if (index <= 0) return;
    const next = [...this._selectedIds()];
    const id = next[index];
    moveItemInArray(next, index, index - 1);
    this.apply(next);
    this.itemMoved.emit({ id, from: index + 1, to: index, total: next.length });
  }

  protected moveDown(index: number): void {
    const next = [...this._selectedIds()];
    if (index >= next.length - 1) return;
    const id = next[index];
    moveItemInArray(next, index, index + 1);
    this.apply(next);
    this.itemMoved.emit({
      id,
      from: index + 1,
      to: index + 2,
      total: next.length,
    });
  }

  protected drop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    const next = [...this._selectedIds()];
    const id = next[event.previousIndex];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.apply(next);
    this.itemMoved.emit({
      id,
      from: event.previousIndex + 1,
      to: event.currentIndex + 1,
      total: next.length,
    });
  }

  protected removeAt(index: number): void {
    const current = this._selectedIds();
    const id = current[index];
    if (!id) return;
    const next = current.filter((_, i) => i !== index);
    this.apply(next);
    this.itemRemoved.emit({ id, position: index + 1, total: next.length });
  }

  private apply(next: string[]): void {
    this._selectedIds.set(next);
    this.selectedIdsChange.emit(next);
  }
}
